import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { ChevronLeft, Pencil, RotateCcw, Info } from 'lucide-react';
import { PageContainer } from '@/components/page-container';
import {
  useSandboxSessionsStream,
  type SandboxSessionDTO,
} from '@/hooks/use-sandbox-sessions-stream';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip';
import { DeliveriesPanel } from '@/components/deliveries/deliveries-panel';
import { midEllipsisUrl } from '@/lib/mid-ellipsis';
import {
  WebhookEditDialog,
  ResetToCliDialog,
  CopyButton,
  type SandboxSession,
} from '@/pages/sandbox';

export function SandboxSessionDetailPage() {
  const { sessionId } = useParams<{ sessionId: string }>();
  const navigate = useNavigate();
  const { sessions, applySession } = useSandboxSessionsStream();

  const session: SandboxSessionDTO | null = useMemo(() => {
    if (!sessionId) return null;
    return sessions.find((s) => s.id === sessionId) ?? null;
  }, [sessions, sessionId]);

  const [editOpen, setEditOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);

  if (!sessionId) {
    return <div className="p-8 text-sm text-muted-foreground">Missing session id.</div>;
  }

  const tunnelUrl =
    session?.hostname ? `https://${session.hostname}/webhook` : null;
  const hasCustomUrl =
    !!session?.webhookUrl && session.webhookUrl !== tunnelUrl;
  const usingCli = !hasCustomUrl;

  // Channel-aware page title — phone for WA, @username for IG (falls back
  // to the IGSID while the username resolver hasn't populated, then to a
  // plain "Sandbox Session" while the row is still loading from the SSE
  // snapshot).
  const sessionLabel = (() => {
    if (!session) return '';
    if (session.type === 'instagram') {
      if (session.senderInstagramUsername) {
        return `: @${session.senderInstagramUsername}`;
      }
      if (session.senderInstagramId) return `: ${session.senderInstagramId}`;
      return '';
    }
    if (session.type === 'facebook') {
      return session.facebookSenderName ? `: ${session.facebookSenderName}` : '';
    }
    return session.phone ? `: ${session.phone}` : '';
  })();

  return (
    <PageContainer variant="fill" className="gap-4">
      <Button
        variant="outline"
        size="icon"
        className="self-start"
        onClick={() => navigate(-1)}
        aria-label="Go back"
      >
        <ChevronLeft className="h-5 w-5" />
      </Button>
      <h1 className="text-2xl font-semibold tracking-tight">
        Sandbox Session{sessionLabel}
      </h1>

      <section className="space-y-3">
        <dl className="grid grid-cols-1 gap-3 text-sm">
          <div className="flex items-start gap-3">
            <dt className="w-24 shrink-0 text-muted-foreground">Session ID</dt>
            <dd className="flex-1 min-w-0 font-mono text-xs">{sessionId}</dd>
          </div>

          {session?.accessToken && (
            <div className="flex items-start gap-3">
              <dt className="w-24 shrink-0 text-muted-foreground pt-1">
                Bind code
              </dt>
              <dd className="flex-1 min-w-0">
                <div className="flex items-center gap-1">
                  <span
                    className="font-mono text-sm"
                    title={session.accessToken}
                  >
                    {session.accessToken}
                  </span>
                  <CopyButton text={session.accessToken} />
                </div>
              </dd>
            </div>
          )}

          <div className="flex items-start gap-3">
            <dt className="w-24 shrink-0 text-muted-foreground pt-1">Webhook</dt>
            <dd className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                {usingCli ? (
                  <>
                    <span className="text-sm font-medium">HookMyApp CLI</span>
                    <Tooltip>
                      <TooltipTrigger
                        render={
                          <button
                            type="button"
                            aria-label="About the HookMyApp CLI flow"
                            className="inline-flex text-muted-foreground hover:text-foreground"
                          />
                        }
                      >
                        <Info className="size-3.5" />
                      </TooltipTrigger>
                      <TooltipContent>
                        Messages are delivered to your app by the HookMyApp CLI.
                      </TooltipContent>
                    </Tooltip>
                  </>
                ) : (
                  <>
                    <span
                      className="text-sm"
                      title={session!.webhookUrl ?? ''}
                    >
                      {midEllipsisUrl(session!.webhookUrl ?? '')}
                    </span>
                    <CopyButton text={session!.webhookUrl ?? ''} />
                  </>
                )}
                <Button
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => setEditOpen(true)}
                  disabled={!session}
                  aria-label="Edit webhook URL"
                  title="Edit webhook URL"
                >
                  <Pencil className="size-3.5" />
                </Button>
                {hasCustomUrl ? (
                  <Button
                    variant="ghost"
                    size="icon-xs"
                    onClick={() => setResetOpen(true)}
                    disabled={!session}
                    aria-label="Switch to CLI"
                    title="Switch to CLI"
                  >
                    <RotateCcw className="size-3.5" />
                  </Button>
                ) : null}
              </div>
            </dd>
          </div>
        </dl>
      </section>

      <DeliveriesPanel scope={`sandbox-session:${sessionId}`} />

      <WebhookEditDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        session={session as SandboxSession | null}
        onSaved={(updated) => {
          applySession(updated);
          setEditOpen(false);
        }}
      />

      <ResetToCliDialog
        open={resetOpen}
        onOpenChange={setResetOpen}
        session={session as SandboxSession | null}
        onSaved={(updated) => {
          applySession(updated);
          setResetOpen(false);
        }}
      />
    </PageContainer>
  );
}
